/**
 * Per-account billing identity (per-account fee/billing arc, Inc 2b frontend).
 *
 * One row per account currently related to an agreement (direct + indirect),
 * carrying the legal payee identity (name / TIN / address) for that account's
 * fee invoice. All drift + completeness flags are SERVER-DERIVED here — the
 * frontend renders them, it never recomputes them (arc spec Inc 2; CONCLUSIONS
 * C-214). Admin-only surface.
 */
import { api } from './client';
import { downloadBlob } from './iaaExports';
import type { ISODate, UUID } from './types';

export interface BillingIdentityRow {
  account_id: UUID;
  is_direct: boolean;
  status: string;                 // the account's lifecycle status (Active / Inactive)
  insurer: string | null;         // indirect only
  policy_number: string | null;   // indirect only
  bank_name: string | null;
  account_number: string | null;

  billing_person_id: UUID | null;
  billing_name: string | null;
  billing_tin: string | null;
  billing_address: string | null;

  name_drift: boolean;            // stored != snapshotted person's CURRENT value
  tin_drift: boolean;
  address_drift: boolean;
  is_complete: boolean;           // all three billing fields non-empty

  // Per-account fee assignment (Fee Schedule model, Slice 3d). Born empty;
  // Admin-set; cleared on detach. fee_schedule_name is None until a schedule
  // is assigned (server-resolved — never re-derive the name client-side).
  fee_schedule_id: UUID | null;
  fee_schedule_name: string | null;
  vat_rate_bps: number | null;
  fee_frequency: string | null;   // per-account billing cadence (Slice-4 #2/#7), beside VAT
  // Basis-vs-assignment info flags (Slice 5.6 / redesign §8) — only for explicit
  // "Choose accounts" basis schedules (automatic co-assigned can't mismatch).
  assigned_not_in_basis: boolean;      // billed under a schedule whose basis excludes this account
  in_basis_billed_elsewhere: boolean;  // feeds a schedule's basis but billed under a different one
}

export interface BillingIdentityManualUpdate {
  billing_name: string | null;
  billing_tin: string | null;
  billing_address: string | null;
}

/** Both null clears the assignment. The schedule must be available to the
 *  agreement (400 otherwise — the picker itself is pre-filtered so this
 *  shouldn't normally fire). */
export interface BillingIdentityAssignmentUpdate {
  fee_schedule_id: UUID | null;
  vat_rate_bps: number | null;
  fee_frequency: string | null;   // one of monthly|quarterly|semiannual, or null to clear
}

/** Read-mode resulting fee for one account (Slice 5.6 (a)) — what it would be billed
 *  at the latest month with AuM, under its SAVED assignment. Accounts missing
 *  AuM/frequency for that month are absent (the UI shows "—"). */
export interface AccountFeePreview {
  account_id: UUID;
  period_month: ISODate;
  aum_basis_cents: number | null;
  net_fee_cents: number;
  vat_cents: number;
  total_fee_cents: number;
  effective_rate_bps: number | null;
}

/** One row of the firm-wide Billing-details table (Admin oversight surface,
 *  OPEN_ITEMS #8): the per-agreement billing row + its owning-agreement label +
 *  its resulting-fee preview. Fee fields are null when the account is unassigned
 *  or has no computable fee at the latest AuM month; `missing_latest_aum` flags the
 *  assigned-but-not-computing case (revenue-leak oversight). Admin-only. */
export interface FirmWideBillingRow extends BillingIdentityRow {
  agreement_id: UUID;
  agreement_code: string | null;      // code_1 (e.g. "CIIF-301"); provisional "CIIF-PRO" for Prospects
  agreement_mnemonic: string;
  agreement_status: string;           // P1: "Prospect" rows are listed for assignment, never billed

  fee_period_month: ISODate | null;
  aum_basis_cents: number | null;
  net_fee_cents: number | null;
  vat_cents: number | null;
  total_fee_cents: number | null;
  effective_rate_bps: number | null;
  missing_latest_aum: boolean;
}

/** One basis peer of an account on a tranches schedule (mini-design A): an account
 *  whose fee-basis AuM consolidates to set the inspected account's blended rate. */
export interface BasisPeer {
  account_id: UUID;
  account_label: string | null;
  is_direct: boolean;
  agreement_id: UUID | null;
  agreement_code: string | null;
  agreement_mnemonic: string | null;
}

export const accountBillingApi = {
  /** One row per currently-related account, with billing identity + drift + completeness. */
  list: (agreementId: UUID) =>
    api.get<BillingIdentityRow[]>(`/account-billing-identities/${agreementId}`),

  /** Firm-wide flat Billing-details table across ALL agreements (Admin-only). One row
   *  per related account with identity + assignment + basis flags + resulting-fee
   *  preview + a missing-AuM flag. Returned whole (<=250 rows) — sort/filter in-browser. */
  listFirmWide: () =>
    api.get<FirmWideBillingRow[]>(`/account-billing-identities/firm-wide`),

  /** The accounts whose consolidated fee-basis AuM sets this account's blended rate
   *  (Admin-only; empty for unassigned / flat / fixed). Lazily fetched on row-expand. */
  basisPeers: (agreementId: UUID, accountId: UUID) =>
    api.get<BasisPeer[]>(`/account-billing-identities/${agreementId}/${accountId}/basis-peers`),

  /** Export the firm-wide billing table as .xlsx (Admin-only). `keys` = the on-screen
   *  rows in display order so the sheet mirrors the current filtered/sorted view;
   *  [] exports every row. Streams a blob → browser save. */
  exportFirmWide: async (keys: { agreement_id: UUID; account_id: UUID }[]): Promise<void> => {
    const res = await fetch('/account-billing-identities/firm-wide/export', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ keys }),
    });
    if (!res.ok) throw new Error('The billing export could not be generated.');
    downloadBlob(await res.blob(), 'billing-details-firm-wide.xlsx');
  },

  /** Manual edit of name / TIN / address (direct-write, audited, no maker-checker). */
  update: (agreementId: UUID, accountId: UUID, body: BillingIdentityManualUpdate) =>
    api.put<BillingIdentityRow>(`/account-billing-identities/${agreementId}/${accountId}`, body),

  /** Copy a chosen person's name / TIN / address into the row (records billing_person_id). */
  snapshot: (agreementId: UUID, accountId: UUID, personId: UUID) =>
    api.post<BillingIdentityRow>(
      `/account-billing-identities/${agreementId}/${accountId}/snapshot`,
      { person_id: personId },
    ),

  /** Assign (or clear) the account's fee schedule + VAT. User-read / Admin-write,
   *  like the rest of the Billing-details row. */
  setAssignment: (agreementId: UUID, accountId: UUID, body: BillingIdentityAssignmentUpdate) =>
    api.put<BillingIdentityRow>(`/account-billing-identities/${agreementId}/${accountId}/assignment`, body),

  /** Read-mode resulting fee per account (saved assignments, latest AuM month). */
  feePreview: (agreementId: UUID): Promise<AccountFeePreview[]> =>
    api.get<AccountFeePreview[]>(`/account-billing-identities/${agreementId}/fee-preview`),
};
